/**
 * Pure TEA update for entity occurrence scans, Harper lexicon refresh, and the
 * debounce triggers that start them.
 *
 * Shell: `useEditorHeadingsStore.ts` (Pinia + file reads, Harper sync, timers).
 * The shell snapshots session facts into Msgs; this module decides whether to
 * skip, clear, drop a stale result, or emit the next Cmd.
 */
import { assertNever } from "../lib/assertNever";
import type { EntityFrontmatter } from "../lib/entityFrontmatter";
import { isJust, isNothing, just, nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";
import type { EntityOccurrence } from "../editor/entityOccurrences";

export type EditorHeadingsModel = {
  readonly entityOccurrences: EntityOccurrence[];
};

export const initialEditorHeadingsModel: EditorHeadingsModel = {
  entityOccurrences: [],
};

/** The Database entry an occurrence scan is for. */
export type OccurrenceTarget = {
  readonly root: string;
  readonly dbName: string;
};

/** Session state the occurrence guard depends on, snapshotted by the shell. */
export type OccurrenceSessionFacts = {
  readonly root: Maybe<string>;
  readonly hasProjectConfig: boolean;
  readonly activeDatabase: Maybe<string>;
  readonly activeMap: boolean;
};

/** Names that count as an occurrence of the entry (display name + aliases). */
export type OccurrenceEntry = {
  readonly name: string;
  readonly aka: readonly string[];
  readonly plural: Maybe<string>;
  readonly adjective: Maybe<string>;
};

/** The open Database entry's live (unsaved) state, fed to the Harper lexicon. */
export type HarperLiveDatabase = {
  readonly fileName: string;
  readonly doc: string;
  readonly fields: Maybe<EntityFrontmatter>;
};

/**
 * The scan target when a project, root, and Database entry are open and no map
 * is showing. `nothing` means there is nothing to scan.
 */
export function occurrenceTarget(
  facts: OccurrenceSessionFacts,
): Maybe<OccurrenceTarget> {
  if (
    isJust(facts.root) &&
    facts.hasProjectConfig &&
    isJust(facts.activeDatabase) &&
    facts.activeMap === false
  ) {
    return just({ root: facts.root.value, dbName: facts.activeDatabase.value });
  }
  return nothing;
}

export type EditorHeadingsMsg =
  /** Plain setter (project switches / parks clear the list). */
  | {
      readonly type: "OccurrencesSet";
      readonly occurrences: EntityOccurrence[];
    }
  | {
      readonly type: "RefreshOccurrences";
      /** Share sessions cannot list Database entries; skip all scans. */
      readonly shareSkips: boolean;
      readonly facts: OccurrenceSessionFacts;
    }
  | {
      readonly type: "EntryFieldsFetched";
      readonly requested: OccurrenceTarget;
      readonly entry: OccurrenceEntry;
      /** Session facts at the moment the fetch resolved (stale check). */
      readonly current: OccurrenceSessionFacts;
      /** Scene files in manuscript order at the moment the fetch resolved. */
      readonly files: readonly string[];
    }
  | {
      readonly type: "OccurrencesScanned";
      readonly occurrences: EntityOccurrence[];
    }
  | {
      readonly type: "RefreshHarperLexicon";
      readonly shareSkips: boolean;
      readonly root: Maybe<string>;
      readonly live: Maybe<HarperLiveDatabase>;
    }
  | { readonly type: "ScheduleHarperRefresh"; readonly shareSkips: boolean }
  | { readonly type: "ScheduleOccurrencesRefresh" }
  | { readonly type: "Dispose" };

export type EditorHeadingsCmd =
  | { readonly kind: "fetchEntryFields"; readonly target: OccurrenceTarget }
  | {
      readonly kind: "scanOccurrences";
      readonly root: string;
      readonly files: readonly string[];
      readonly entry: OccurrenceEntry;
    }
  | {
      readonly kind: "syncHarperLexicon";
      readonly root: Maybe<string>;
      readonly live: Maybe<HarperLiveDatabase>;
    }
  | { readonly kind: "scheduleHarperRefresh" }
  | { readonly kind: "scheduleOccurrencesRefresh" }
  | { readonly kind: "clearTimers" };

export function updateEditorHeadings(
  model: EditorHeadingsModel,
  msg: EditorHeadingsMsg,
): readonly [EditorHeadingsModel, readonly EditorHeadingsCmd[]] {
  switch (msg.type) {
    case "OccurrencesSet":
      return [{ entityOccurrences: msg.occurrences }, []];
    case "RefreshOccurrences": {
      const target = occurrenceTarget(msg.facts);
      if (msg.shareSkips === true || isNothing(target)) {
        return [{ entityOccurrences: [] }, []];
      }
      return [model, [{ kind: "fetchEntryFields", target: target.value }]];
    }
    case "EntryFieldsFetched": {
      // A file switch (or project close) may have landed while the fields
      // fetch was in flight. Re-check everything the first guard checked.
      const current = occurrenceTarget(msg.current);
      if (
        isNothing(current) ||
        current.value.root !== msg.requested.root ||
        current.value.dbName !== msg.requested.dbName
      ) {
        return [model, []];
      }
      return [
        model,
        [
          {
            kind: "scanOccurrences",
            root: msg.requested.root,
            files: msg.files,
            entry: msg.entry,
          },
        ],
      ];
    }
    case "OccurrencesScanned":
      return [{ entityOccurrences: msg.occurrences }, []];
    case "RefreshHarperLexicon":
      // Readonly share: no spellcheck, and Database invokes are forbidden.
      if (msg.shareSkips === true) return [model, []];
      return [
        model,
        [{ kind: "syncHarperLexicon", root: msg.root, live: msg.live }],
      ];
    case "ScheduleHarperRefresh":
      if (msg.shareSkips === true) return [model, []];
      return [model, [{ kind: "scheduleHarperRefresh" }]];
    case "ScheduleOccurrencesRefresh":
      return [model, [{ kind: "scheduleOccurrencesRefresh" }]];
    case "Dispose":
      return [model, [{ kind: "clearTimers" }]];
    default:
      return assertNever(msg);
  }
}

export const updateEditorHeadingsFn: UpdateFn<
  EditorHeadingsModel,
  EditorHeadingsMsg,
  EditorHeadingsCmd
> = updateEditorHeadings;
